<template>
  <section>
    <NavTop ref="topNav" @toggle-bar="toggleBar" />

    <div
      v-show="isLoaded && toggleClass"
      class="custom-sidebar"
      :class="{ active: toggleClass, inactive: !toggleClass }"
    >
      <ul class="nav flex-column mb-0">
        <li class="nav-item">
          <a href="#" class="nav-link font-italic" @click.prevent="go('')">
            <font-awesome-icon icon="th-large" class="mr-3" />
            <span class="custom-menu-link">{{ t('left.link.home') }}</span>
          </a>
        </li>

        <li
          class="nav-item"
          v-if="!isRagOnlyUser"
        >
          <a href="#" class="nav-link font-italic" @click.prevent="newRequest">
            <font-awesome-icon icon="folder-open" class="mr-3" />
            <span class="custom-menu-link">{{ t(newRequestLabel) }}</span>
          </a>
        </li>
        <li v-if="showScanproMenuItem" class="nav-item">
          <a href="#" class="nav-link font-italic" @click.prevent="go('scanpro')">
            <font-awesome-icon icon="qrcode" class="mr-3" />
            <span class="custom-menu-link">ScanPro Assist</span>
          </a>
        </li>
        <li v-if="!isMahle() && !isRagOnlyUser" class="nav-item">
          <a href="#" class="nav-link font-italic" @click.prevent="go('location')">
            <font-awesome-icon icon="warehouse" class="mr-3" />
            <span class="custom-menu-link">{{ t(locationLabel) }}</span>
          </a>
        </li>
        <li v-if="!isMahle() && !isRagOnlyUser" class="nav-item">
          <a href="#" class="nav-link font-italic" @click.prevent="go('user')">
            <font-awesome-icon icon="user" class="mr-3" />
            <span class="custom-menu-link">{{ t('profile.label.title') }}</span>
          </a>
        </li>
        <li v-if="!isMahle() && !isRagOnlyUser" class="nav-item">
          <a href="#" class="nav-link font-italic" @click.prevent="go('about')">
            <font-awesome-icon icon="info" class="mr-3" />
            <span class="custom-menu-link">{{ t(aboutUsLabel) }}</span>
          </a>
        </li>
        <!-- RAG menu item - shown when user has ShowRAG permission -->
        <li class="nav-item" v-if="showRagMenuItem">
          <a
            href="#"
            class="nav-link font-italic"
            @click.prevent="go('rag')"
          >
            <font-awesome-icon icon="bars" class="mr-3" />
            <span class="custom-menu-link">{{ t('menu.label.rag') }}</span>
          </a>
        </li>
        <li class="nav-item">
          <a href="#" class="nav-link font-italic" @click.prevent="logout">
            <font-awesome-icon icon="sign-out-alt" />
            <span class="custom-menu-link">{{ t('logout.label.title') }}</span>
          </a>
        </li>
      </ul>
    </div>

    <div v-show="toggleClass" class="custom-background" @click="closeMenu"></div>

    <div class="container custom-main">
      <router-view />
    </div>

    <div
      v-show="showRightSlideOut"
      id="rightSlideOut"
      class="slideOut slideOutRight"
      :style="slideOutStyle"
    >
      <div class="slideOutTitle">
        <a href="#" @click.prevent="closeSlideOut">
          <font-awesome-icon icon="times" />
        </a>
        <span>{{ slideOutTitle }}</span>
      </div>
      <Chat
        v-if="activeChannelUrl"
        :channel-url="activeChannelUrl"
        :channel-set="channelSet"
        :is-history="false"
      />
    </div>

    <DtConfirm
      v-model="actionConfirm"
      :confirm-title="t(newRequestLabel)"
      :confirm-no-action="t('no')"
      :confirm-action="t('yes')"
      :confirm-question="t('submit.question')"
      @closing="closeModal"
      @proceed="proceedNewRequest"
    />

    <DialogOpeningHours
      v-model:show-dialog="isDialogOpen"
      :support-email="supportEmailAddress"
      @closing="closeDialog"
    />
    <ClientSurveyModal
      v-if="surveyWorkOrderId != null"
      v-model="showSurvey"
      :work-order-id="surveyWorkOrderId"
      @submitted="onSurveyDone"
      @skipped="onSurveyDone"
    />
  </section>
</template>

<script setup>
import { nextTick, ref, computed, onMounted, onBeforeUnmount, watch } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { eventBus } from '@/plugins/eventBus'

import NavTop from '@/components/navigation/nav-top.vue'
import Chat from '@/components/chat/chat.vue'
import DtConfirm from '@/components/utilities/modal/dt-confirm.vue'
import DialogOpeningHours from '@/components/openinghours/dialog-opening-hours.vue'
import ClientSurveyModal from '@/components/survey/ClientSurveyModal.vue'
import { useSendbird } from '@/plugins/sendbird/sendbird-client';
import {
  appId,
  isUK,
  isMahle,
  isStGobain,
  isClever,
  isOpeningHoursEnabled,
  isMahleOpeningHoursEnabled,
  isClientSurveyEnabledForThisTenant,
  setCurrentPage,
  toBool
} from '@/plugins/globals.js'
import { forceLogout } from '@/helpers/auth'

const emit = defineEmits(['closeMenu'])

const store = useStore()
const { t } = useI18n()
const router = useRouter()
const topNav = ref(null)

const toggleClass = ref(false)
const showRightSlideOut = ref(false)
const slideOutStyle = ref('')
const slideOutTitle = ref('')
const isLoaded = ref(false)
const actionConfirm = ref(null)
const isDialogOpen = ref(null)
const supportEmailAddress = ref('')
const showSurvey = ref(false)
const surveyWorkOrderId = ref(null) // number|null

const { getActiveChannel, getChannelInfo } = useSendbird();

const tenantDetails = computed(() => store.getters['openingHours/tenantDetails'])
const isOpen = computed(() => store.getters['openingHours/isOpen'])

const activeChannelUrl = computed(() => {
  const channel = getActiveChannel();
  return channel?.url || '';
});
const channelSet = computed(() => {
  const channel = getActiveChannel();
  const channelSet = getChannelInfo(channel);
  return channelSet || {}
});

const showScanproMenuItem = computed(() => {
  return (appId() !== 5 && appId() !== 1) || (appId() === 1 && isStGobain())
})

const locationLabel = computed(() =>
  isClever() ? 'left.link.clever.location' : 'left.link.location'
)

const aboutUsLabel = computed(() =>
  isClever() ? 'left.link.clever.about' : 'left.link.about'
)

const isRagOnlyUser = computed(() => toBool(store.getters['auth/isRagOnly'] || false));
const showRagMenuItem = computed(() => toBool(store.getters['auth/showRag'] || false));

const newRequestLabel = computed(() =>
  isClever() ? 'request.title.clever.newrequest' : 'request.title.newrequest'
)

onMounted(async () => {
  await store.dispatch('openingHours/getTenantDetails')
  isLoaded.value = true

  if (eventBus?.on) {
    eventBus.on('heartbeat', pulseHeartbeat)
    eventBus.on('slideout', showSlideOut)
  }
  if (await isClientSurveyEnabledForThisTenant()) {
    setTimeout(() => {
      tryOpenPendingSurvey()
    }, 250)
  }
})

onBeforeUnmount(() => {
  if (eventBus?.off) {
    eventBus.off('heartbeat', pulseHeartbeat)
    eventBus.off('slideout', showSlideOut)
  }
})

watch(showSurvey, (open) => {
  if (!open && surveyWorkOrderId.value != null) {
    localStorage.removeItem('survey_pending_workOrderId')
    localStorage.removeItem('survey_pending_setAt')
    surveyWorkOrderId.value = null
  }
})

function pulseHeartbeat(event) {
  console.log('An Event Occurred', event)
}

function go(where) {
  emit('closeMenu')
  toggleClass.value = false

  if (isRagOnlyUser.value && where !== 'rag') {
    return
  }
  router.push(`/${where}`)
}

function logout() {
  forceLogout()
  window.location.assign('/')
}

function toggleBar(toggle) {
  toggleClass.value = toggle
}

function closeMenu() {
  topNav.value?.closeMenu()
}

function closeModal() {
  actionConfirm.value = null
}

function closeDialog() {
  isDialogOpen.value = false
}

function getTenantDetailsArray() {
  try {
    return typeof tenantDetails.value === 'string'
      ? JSON.parse(tenantDetails.value)?.Items || []
      : tenantDetails.value?.Items || []
  } catch {
    return []
  }
}

async function newRequest() {
  const tenant = getTenantDetailsArray()
  const openingEnabled = await isOpeningHoursEnabled()
  const mahleOpeninEnabled = await isMahleOpeningHoursEnabled()

  if (openingEnabled || mahleOpeninEnabled) {
    const shopType = openingEnabled ? tenant[0]?.ID : tenant[1]?.ID
    await store.dispatch('openingHours/isShopOpen', shopType)
    toggleClass.value = false

    if (isOpen.value) {
      isDialogOpen.value = null
      isUK() ? proceedNewRequest() : (actionConfirm.value = true)
    } else {
      supportEmailAddress.value = tenant[shopType - 1]?.SupportEmail || ''
      isDialogOpen.value = true
    }
    return
  }

  toggleClass.value = false
  isUK() ? proceedNewRequest() : (actionConfirm.value = true)
}

async function proceedNewRequest() {
  actionConfirm.value = null
  await setCurrentPage('Submit')
  await store.dispatch('serviceRequests/setNewRequest')
  router.replace('/servicerequest/0')
}

function closeSlideOut() {
  showRightSlideOut.value = false
  slideOutStyle.value = ''
  slideOutTitle.value = ''
  eventBus?.emit('closeSlideOut')
}

function showSlideOut(options) {
  if (options.direction === 'right') {
    slideOutStyle.value = options.style
    slideOutTitle.value = options.title
    showRightSlideOut.value = true
  }
}
function tryOpenPendingSurvey() {
  const raw = localStorage.getItem('survey_pending_workOrderId')
  const workOrderId = parseInt(raw || '0', 10)

  if (!Number.isInteger(workOrderId) || workOrderId <= 0) return

  // Optional TTL so you don’t open it days later (same logic as your Vue2)
  const setAt = parseInt(localStorage.getItem('survey_pending_setAt') || '0', 10)
  const tooOld = setAt && (Date.now() - setAt > 10 * 60 * 1000) // 10 mins
  if (tooOld) {
    localStorage.removeItem('survey_pending_workOrderId')
    localStorage.removeItem('survey_pending_setAt')
    return
  }

  surveyWorkOrderId.value = workOrderId

  nextTick(() => {
    showSurvey.value = true
  })
}

function onSurveyDone() {
  // clear the pending flag once user acts (same as Vue2)
  localStorage.removeItem('survey_pending_workOrderId')
  localStorage.removeItem('survey_pending_setAt')

  showSurvey.value = false
  surveyWorkOrderId.value = null
}
</script>

<style lang="scss" scoped>
.custom-main {
  margin-top: 5px;
  padding: 10px;
}
.custom-background {
  position: absolute;
  width: 100%;
  background: rgba(0, 0, 0, 0.2);
  z-index: 99998;
  height: -moz-calc(100% - (6rem));
  height: -webkit-calc(100% - (6rem));
  height: calc(100% - (6rem));
}
.hide-it {
  display: none;
}
.custom-sidebar {
  position: absolute;
  width: 17rem;
  background-color: rgb(121, 121, 121);
  z-index: 99999;
  height: -moz-calc(100% - (6rem));
  height: -webkit-calc(100% - (6rem));
  height: calc(100% - (6rem));
  transform: translateX(-100%);
  -webkit-transform: translateX(-100%);
  li {
    padding-bottom: 1px;
    border-bottom: 1px solid #b6b6b6;
    white-space: nowrap;
  }
  a {
    font-size: 1.2em;
    font-weight: 600;
    color: #ffffff;
  }
  a:hover {
    color: #51b04a;
  }
}
.active {
  animation: slide-in 0.5s forwards;
  -webkit-animation: slide-in 0.5s forwards;
}
.inactive {
  animation: slide-out 0.5s forwards;
  -webkit-animation: slide-out 0.5s forwards;
}
@keyframes slide-in {
  100% {
    transform: translateX(0%);
  }
}
@-webkit-keyframes slide-in {
  100% {
    -webkit-transform: translateX(0%);
  }
}
@keyframes slide-out {
  0% {
    transform: translateX(0%);
  }
  100% {
    transform: translateX(-100%);
  }
}
@-webkit-keyframes slide-out {
  0% {
    -webkit-transform: translateX(0%);
  }
  100% {
    -webkit-transform: translateX(-100%);
  }
}
.custom-menu-link {
  position: absolute;
  left: 70px;
}
.slideOut {
  background-color: #ffffff;
  position: absolute;
  z-index: 10001;
  -moz-box-shadow: 0px 10px 18px 0px #ccc;
  -webkit-box-shadow: 0px 10px 18px 0px #ccc;
  box-shadow: 0px 10px 18px 0px #ccc;
}
.slideOutTitle {
  position: relative;
  height: 44px;
  width: 100%;
  background-color: #338c2f;
  color: #ffffff;
  font-weight: 500;
  border-bottom: 1px solid #686868;
  margin: 0;
  padding-top: 6px;
  vertical-align: middle;
  text-align: center;
  font-size: 1.2em;
  a {
    position: absolute;
    color: #ffffff;
    top: 4px;
    left: 4px;
    height: 28px;
    width: 28px;
  }
}
.slideOutRight {
  display: flex;
  flex-direction: column;
  right: 0;
  top: 0;
  height: 100%;
  border-top: 1px solid #686868;
  border-left: 1px solid #686868;
  border-bottom: 1px solid #686868;
  overflow: hidden;
}
</style>
